import { useState, useEffect, useCallback } from 'react';
import { adminApi } from '../../lib/adminApi';
import { toast } from 'sonner';
import {
  Users,
  Buildings,
  Link as LinkIcon,
  CursorClick,
  ArrowClockwise,
  Warning,
  Lightning,
  TrendUp,
} from '@phosphor-icons/react';
import { Button } from '../../components/ui/button';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from 'recharts';

const TooltipChrome = (
  <Tooltip
    cursor={{ stroke: '#4648d4', strokeOpacity: 0.15, strokeWidth: 1 }}
    contentStyle={{
      fontFamily: "'Inter', sans-serif",
      fontSize: 12,
      border: '1px solid hsl(var(--border))',
      borderRadius: 12,
      boxShadow: '0 18px 40px -16px rgba(11,28,48,0.12)',
      padding: '8px 12px',
    }}
  />
);

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(false);
      const stats = await adminApi.getStats();
      setData(stats);
    } catch (err) {
      console.error('Failed to load admin dashboard', err);
      setError(true);
      toast.error('Failed to load admin dashboard data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <div className="p-6 md:p-10 animate-fade-in max-w-[1280px] mx-auto w-full">
        <div className="h-9 w-64 bg-muted rounded-xl animate-pulse mb-8" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-32 surface-card animate-pulse" />
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="h-80 surface-card animate-pulse" />
          <div className="h-80 surface-card animate-pulse" />
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="p-6 md:p-10 animate-fade-in" data-testid="admin-dashboard-error">
        <div className="surface-card p-12 flex flex-col items-center justify-center text-center max-w-2xl mx-auto">
          <div className="grid place-items-center h-14 w-14 rounded-2xl bg-destructive/10 text-destructive mb-4">
            <Warning size={26} weight="fill" />
          </div>
          <h2 className="font-display text-2xl font-extrabold text-foreground mb-2">
            Failed to load admin dashboard
          </h2>
          <p className="text-sm text-muted-foreground mb-6 max-w-md">
            We couldn't fetch platform statistics. Check your connection and try again.
          </p>
          <Button
            onClick={fetchData}
            data-testid="retry-btn"
            className="rounded-xl gradient-primary text-white shadow-glow hover:opacity-95 px-6 py-2.5 font-semibold gap-2"
          >
            <ArrowClockwise size={16} weight="bold" />
            Retry
          </Button>
        </div>
      </div>
    );
  }

  const stats = [
    {
      label: 'Total users',
      value: data.total_users,
      icon: Users,
      tone: 'primary',
    },
    {
      label: 'Total workspaces',
      value: data.total_workspaces,
      icon: Buildings,
      tone: 'success',
    },
    {
      label: 'Total links',
      value: data.total_links,
      icon: LinkIcon,
      tone: 'warning',
    },
    {
      label: 'Total clicks',
      value: data.total_clicks,
      icon: CursorClick,
      tone: 'pink',
    },
  ];

  const toneStyles = {
    primary: { bg: 'bg-primary/10', icon: 'text-primary' },
    success: { bg: 'bg-emerald-100', icon: 'text-emerald-600' },
    warning: { bg: 'bg-amber-100', icon: 'text-amber-600' },
    pink: { bg: 'bg-pink-100', icon: 'text-pink-600' },
  };

  return (
    <div className="p-6 md:p-10 animate-fade-in max-w-[1280px] mx-auto w-full" data-testid="admin-dashboard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="font-display text-3xl sm:text-[2.5rem] font-extrabold tracking-tight text-foreground leading-tight">
            Platform overview
          </h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-xl">
            <span className="font-medium text-foreground/90">Admin console</span>
            {' · '}
            High-level metrics across every workspace, account, and link on LinkFlow.
          </p>
        </div>
        <div className="flex items-center gap-2.5">
          <Button
            onClick={fetchData}
            variant="outline"
            className="rounded-xl border-border bg-white h-11 px-5 gap-2"
            data-testid="admin-refresh-btn"
          >
            <ArrowClockwise size={16} weight="bold" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8 stagger-children">
        {stats.map((s) => {
          const Icon = s.icon;
          const tone = toneStyles[s.tone];
          return (
            <div
              key={s.label}
              className="surface-card p-5 surface-card-hover relative overflow-hidden ambient-shadow border-border/90 hover:border-primary/25 transition-colors"
              data-testid={`stat-${s.label.toLowerCase().replace(/\s/g, '-')}`}
            >
              <div className="flex items-center justify-between mb-3">
                <span className={`grid place-items-center h-10 w-10 rounded-xl ${tone.bg} ${tone.icon}`}>
                  <Icon size={18} weight="fill" />
                </span>
                <span className="chip bg-muted text-muted-foreground text-[10px]">
                  Lifetime
                </span>
              </div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                {s.label}
              </p>
              <p className="font-display text-3xl font-extrabold text-foreground mt-1">
                {s.value?.toLocaleString() || 0}
              </p>
            </div>
          );
        })}
      </div>

      {/* 30-day charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="surface-card p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-display text-lg font-bold text-foreground">
                New registrations
              </h3>
              <p className="text-xs text-muted-foreground mt-0.5">
                Sign-ups over the last 30 days.
              </p>
            </div>
            <span className="chip bg-primary/10 text-primary">
              <Lightning size={12} weight="fill" />
              30d
            </span>
          </div>
          <div className="h-[280px]">
            {data.registrations_30d?.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                  data={data.registrations_30d}
                  margin={{ top: 5, right: 8, left: -16, bottom: 0 }}
                >
                  <defs>
                    <linearGradient id="adminRegGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#4648d4" stopOpacity={0.32} />
                      <stop offset="100%" stopColor="#4648d4" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="4 4" stroke="#EAE6F7" vertical={false} />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 11, fill: '#6B6B7A' }}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(v) => v?.slice(5)}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: '#6B6B7A' }}
                    tickLine={false}
                    axisLine={false}
                    width={42}
                    allowDecimals={false}
                  />
                  {TooltipChrome}
                  <Area
                    type="monotone"
                    dataKey="count"
                    name="Sign-ups"
                    stroke="#4648d4"
                    fill="url(#adminRegGrad)"
                    strokeWidth={2.5}
                    activeDot={{ r: 5, strokeWidth: 2, stroke: '#FFFFFF' }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <EmptyChart label="No registration data yet." />
            )}
          </div>
        </div>

        <div className="surface-card p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-display text-lg font-bold text-foreground">
                Links created
              </h3>
              <p className="text-xs text-muted-foreground mt-0.5">
                Short-link creation across the platform.
              </p>
            </div>
            <span className="chip bg-amber-100 text-amber-700">
              <TrendUp size={12} weight="bold" />
              30d
            </span>
          </div>
          <div className="h-[280px]">
            {data.links_created_30d?.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                  data={data.links_created_30d}
                  margin={{ top: 5, right: 8, left: -16, bottom: 0 }}
                >
                  <defs>
                    <linearGradient id="adminLinksGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#F59E0B" stopOpacity={0.32} />
                      <stop offset="100%" stopColor="#F59E0B" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="4 4" stroke="#EAE6F7" vertical={false} />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 11, fill: '#6B6B7A' }}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(v) => v?.slice(5)}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: '#6B6B7A' }}
                    tickLine={false}
                    axisLine={false}
                    width={42}
                    allowDecimals={false}
                  />
                  {TooltipChrome}
                  <Area
                    type="monotone"
                    dataKey="count"
                    name="Links"
                    stroke="#F59E0B"
                    fill="url(#adminLinksGrad)"
                    strokeWidth={2.5}
                    activeDot={{ r: 5, strokeWidth: 2, stroke: '#FFFFFF' }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <EmptyChart label="No link creation data yet." />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function EmptyChart({ label }) {
  return (
    <div className="h-full min-h-[180px] flex items-center justify-center text-center">
      <p className="text-sm text-muted-foreground max-w-xs">{label}</p>
    </div>
  );
}
